"use client";

import { useState, type FormEvent, type ReactNode } from "react";
import { FiliumLogo } from "@/components/brand/FiliumLogo";

type AuthCardProps = {
  title: string;
  cta: string;
  onSubmit: (email: string, password: string) => Promise<void>;
  footer: ReactNode;
  /** Shown instead of the form after a successful submit that needs no redirect. */
  done?: ReactNode | null;
  /** Optional signup-only fields rendered between email and password. */
  extraFields?: ReactNode;
};

/** Shared glass card for login/signup. Errors are typed SignalErrors — show .message. */
export function AuthCard({
  title,
  cta,
  onSubmit,
  footer,
  done,
  extraFields,
}: AuthCardProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await onSubmit(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setPending(false);
    }
  }

  const field =
    "w-full rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm text-white placeholder:text-white/30 outline-none transition focus:border-fuchsia-400/50";

  return (
    <main className="relative z-10 flex min-h-dvh items-center justify-center px-6">
      <div className="glass relative w-full max-w-md overflow-hidden rounded-2xl p-10 shadow-[0_10px_50px_-12px_rgba(0,0,0,0.7)]">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent"
        />
        <div className="mb-6 flex justify-center">
          <FiliumLogo size={28} />
        </div>
        {done ? (
          <div className="text-center">{done}</div>
        ) : (
          <>
            <h1 className="display text-center text-2xl">{title}</h1>
            <form onSubmit={handleSubmit} className="mt-8 space-y-4">
              <input
                type="email"
                required
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={field}
              />
              {extraFields}
              <input
                type="password"
                required
                minLength={8}
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={field}
              />
              {error && (
                <p role="alert" className="text-sm text-rose-300">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={pending}
                className="w-full rounded-full border border-fuchsia-400/30 bg-fuchsia-400/10 px-6 py-2.5 text-sm font-medium text-fuchsia-100 transition hover:bg-fuchsia-400/20 disabled:opacity-50"
              >
                {pending ? "…" : cta}
              </button>
            </form>
          </>
        )}
        <div className="mt-6 text-center text-sm text-[var(--muted)]">
          {footer}
        </div>
      </div>
    </main>
  );
}
